import styled from 'styled-components';

import { Text } from '../Text/Text';
import { ANIM, COLORS, REDUCED_MOTION, SPACE } from '../../theme/tokens';

/** The toggle's width: the column the chevrons sit in, which every page's name starts after. */
const TOGGLE_WIDTH = SPACE[28];

export const Root = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;
`;

/** The search field with its magnifier inside, on the left (the SEO tab's page list draws the same). */
export const SearchBox = styled.div`
  position: relative;
  display: flex;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: ${SPACE[12]};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: ${COLORS.textMuted};
  pointer-events: none;
`;

/** The box a `Field` draws, a little shorter: it heads a list rather than a form. */
export const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  box-sizing: border-box;
  /* The left padding clears the magnifier: its 12px inset, its 16px, then 8px of air. */
  padding: 0 ${SPACE[12]} 0 ${SPACE[36]};
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-family: inherit;
  /* 16px, as in a Field: a smaller one makes a phone zoom in on focus. */
  font-size: 16px;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The same focus as a Field: its border in the accent, not the browser's outline. */
  &:focus {
    border-color: ${COLORS.accent};
  }

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const Tree = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  margin: ${SPACE[0]};
  padding: ${SPACE[0]};
  list-style: none;
`;

export const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  min-width: 0;
`;

/** A top page: its branch's toggle (or the toggle's room), then its name. */
export const PageRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const Toggle = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${TOGGLE_WIDTH};
  height: 40px;
  padding: ${SPACE[0]};
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.heading};
  }

  /* Inside the button: a scrolling column would clip a ring drawn outside it. */
  &:focus-visible {
    outline-offset: -2px;
  }
`;

/** The toggle's room on a page with no pages under it, so every name starts in the same place. */
export const Spacer = styled.span`
  flex: none;
  width: ${TOGGLE_WIDTH};
`;

/** Points down while the branch is open, to the side while it is closed. */
export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transform: rotate(${(p) => (p.$open ? '0deg' : '-90deg')});
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** A page's name, which opens it. The open page is in the brand orange (ctaSolid, which follows the owner's accent),
 *  lifted; the top page the open one sits under is in it too, so a closed branch still shows where the owner is. A page under a branch is a little
 *  quieter than the top pages. No state changes the weight: a name never changes width, so nothing around it moves. */
export const Name = styled.button<{ $child?: boolean }>`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  min-height: ${(p) => (p.$child ? '36px' : '40px')};
  padding: ${SPACE[0]} ${SPACE[12]} ${SPACE[0]} ${SPACE[8]};
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${(p) => (p.$child ? COLORS.textMuted : COLORS.text)};
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.heading};
  }

  &[aria-current='true'] {
    color: ${COLORS.ctaSolid};
  }

  &[aria-current='page'] {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.ctaSolid};
  }

  /* Inside the button: a scrolling column would clip a ring drawn outside it. */
  &:focus-visible {
    outline-offset: -2px;
  }
`;

export const Label = styled(Text)`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** How many pages a closed branch holds. */
export const Count = styled.span`
  flex: none;
  color: ${COLORS.textMuted};
  font-size: 12px;
  font-weight: 500;
`;

/** A short note after a page's name ("every page"), as a pill: quieter than the name, never cut. */
export const Hint = styled(Text)`
  flex: none;
  padding: ${SPACE[0]} ${SPACE[8]};
  border: 1px solid ${COLORS.hairline};
  border-radius: 999px;
  color: ${COLORS.textMuted};
  font-size: 11px;
  line-height: 18px;
`;

/** The pages under a top page, on a guide that runs down from under its chevron. The guide sits at the chevron's
 *  centre (half the toggle's width); the names under it are indented past the top page's name (the 1px the guide
 *  takes comes off the padding), so a page under another reads as one. */
export const Branch = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  margin: ${SPACE[0]} ${SPACE[0]} ${SPACE[4]} calc(${TOGGLE_WIDTH} / 2);
  padding: ${SPACE[0]} ${SPACE[0]} ${SPACE[0]} calc(${TOGGLE_WIDTH} / 2 - 1px + ${SPACE[16]});
  border-left: 1px solid ${COLORS.hairline};
  list-style: none;
`;

/** A page's place while the pages are decided: a name's bar where a name would start. */
export const Placeholder = styled.li`
  display: flex;
  align-items: center;
  height: 40px;
  padding-left: calc(${TOGGLE_WIDTH} + ${SPACE[8]});
`;

/** A paragraph: its own margin is cleared and replaced by the design's inset. */
export const Empty = styled(Text)`
  margin: ${SPACE[0]};
  padding: ${SPACE[16]} ${SPACE[8]};
  font-size: 14px;
  color: ${COLORS.textMuted};
`;
